import { CHOSEN_LEAGUE, CHOSEN_TEAM } from 'constants/localStorageKey';
import { championshipLeagues, ROUTES } from 'constants';
import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { createSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation } from 'react-query';
import { api } from 'api/api';
import {
  favoriteTeamsError,
  favoriteTeamsSuccess,
} from 'redux/favoriteTeams/actionCreators.js';
import useLocalStorage from 'hooks';
import types from 'redux/types';

const memoizedUserState = createSelector(
  [(state) => state?.user],
  (user) => user.data
);

const memoizedConfigState = createSelector(
  [(state) => state.configuration],
  (configuration) => configuration.data
);

export const Wrapper = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user_id: userId } = useSelector(memoizedUserState);
  const configuration = useSelector(memoizedConfigState);
  const [searchParams, setSearchParams] = useSearchParams();

  const [savedLeague, setSavedLeague] = useLocalStorage(CHOSEN_LEAGUE, '');
  const [savedTeam] = useLocalStorage(CHOSEN_TEAM, '');

  const params = Object.fromEntries([...searchParams]);
  const url = window.location.href;

  const isUpcomingGames = new RegExp(ROUTES.UPCOMING_GAMES).test(url);

  //** GET ALL FAVORITE TEAMS FOR LOGIN USER **//
  const getAllFavoriteTeams = useMutation(
    (id) => api.favorites.getFavoritesTeams(id),
    {
      onError: () => {
        dispatch(favoriteTeamsError());
      },
      onSuccess: (data) => {
        dispatch(favoriteTeamsSuccess(data.data));
      },
    }
  );

  // ** GET LEAGUES LIST ** //
  useEffect(() => {
    // console.log('[Wrapper] Configuration state:', {
    //   hasConfig: !!configuration,
    //   siteName: configuration?.site_name,
    //   displayLeaguesSelect: configuration?.display_leagues_select,
    //   displayTeamsSelect: configuration?.display_teams_select,
    //   isUpcomingGames,
    //   url,
    // });

    // Handle both string '1' and integer 1 from API
    const shouldShowLeagues =
      configuration?.display_leagues_select === '1' ||
      configuration?.display_leagues_select === 1;

    // Also fetch leagues if we're on the UpcomingGames page, regardless of display_leagues_select
    // This is needed for sites like Madness Time that have display_teams_select=1 but still
    // need leagues data for the UpcomingGames page dropdown
    // if (configuration && (shouldShowLeagues || isUpcomingGames)) {
    //   console.log('[Wrapper] Dispatching GET_LEAGUES_START', {
    //     shouldShowLeagues,
    //     isUpcomingGames,
    //   });
    //   dispatch({
    //     type: types.GET_LEAGUES_START,
    //   });
    // } else {
    //   console.log('[Wrapper] NOT fetching leagues:', {
    //     hasConfig: !!configuration,
    //     shouldShowLeagues,
    //     isUpcomingGames,
    //   });
    // }
    if (configuration && (shouldShowLeagues || isUpcomingGames)) {
      dispatch({
        type: types.GET_LEAGUES_START,
      });
    }
  }, [configuration, dispatch, isUpcomingGames, url]);

  //** GETTING ALL FAVORITE TEAMS IF USER ID IS AVAILABLE **//
  useEffect(() => {
    // Handle both string '1' and integer 1 from API
    const shouldShowLeagues =
      configuration?.display_leagues_select === '1' ||
      configuration?.display_leagues_select === 1;

    if (userId && shouldShowLeagues) {
      getAllFavoriteTeams.mutate(userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, configuration]);

  //** SET SELECTED LEAGUE AND TEAM TO LOCAL STORAGE **//
  useEffect(() => {
    if (params?.league && params?.leagueId) {
      setSavedLeague({ league: params?.league, leagueId: params?.leagueId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params?.league, params?.leagueId, params?.team, params?.teamId]);

  //** SET SELECTED LEAGUE TO URL SEARCH QUERY IF SAVED PREVIOUSLY TO LOCAL STORAGE **//
  useEffect(() => {
    const shouldShowLeagues =
      configuration?.display_leagues_select === '1' ||
      configuration?.display_leagues_select === 1;

    if (
      Boolean(savedLeague) &&
      shouldShowLeagues &&
      championshipLeagues.includes(savedLeague.league) &&
      !params?.league &&
      !params?.leagueId &&
      !isUpcomingGames
    ) {
      navigate(
        `${ROUTES.UPCOMING_GAMES}?league=${savedLeague.league}&leagueId=${savedLeague.leagueId}`,
        { replace: true }
      );
    } else if (
      Boolean(savedLeague) &&
      shouldShowLeagues &&
      !championshipLeagues.includes(savedLeague.league) &&
      !params?.league &&
      !params?.leagueId
    ) {
      const { league, leagueId } = savedLeague;
      setSearchParams(
        (prevValue) => {
          prevValue.set('league', league);
          prevValue.set('leagueId', leagueId);
          return prevValue;
        },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration]);

  //** SET SELECTED TEAM TO URL SEARCH QUERY IF SAVED PREVIOUSLY TO LOCAL STORAGE **//
  useEffect(
    () => {
      const shouldShowLeagues =
        configuration?.display_leagues_select === '1' ||
        configuration?.display_leagues_select === 1;
      const shouldShowTeams =
        configuration?.display_teams_select === '1' ||
        configuration?.display_teams_select === 1;

      if (
        Boolean(savedTeam) &&
        (shouldShowLeagues || shouldShowTeams) &&
        !championshipLeagues.includes(savedLeague.league) &&
        !params?.team &&
        !params?.teamId
      ) {
        const { team, teamId } = savedTeam;
        setSearchParams(
          (prevValue) => {
            prevValue.set('team', team);
            prevValue.set('teamId', teamId);
            return prevValue;
          },
          { replace: true }
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configuration]
  );

  // Auto-set URL params to "Upcoming Events" for team selection sites
  // This is necessary for the initial load - the dropdown will still be usable
  useEffect(() => {
    const shouldShowTeams =
      configuration?.display_teams_select === '1' ||
      configuration?.display_teams_select === 1;
    if (
      shouldShowTeams &&
      !params?.team &&
      configuration?.one_league_name &&
      !Boolean(savedTeam)
    ) {
      setSearchParams(
        (prevValue) => {
          prevValue.set('league', configuration?.one_league_name);
          prevValue.set('leagueId', configuration?.one_league_id);
          prevValue.set('team', 'Upcoming Events');
          prevValue.set('teamId', configuration.one_league_id);
          return prevValue;
        },
        { replace: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configuration?.display_teams_select, configuration?.one_league_name]);

  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{children}</>;
};
